// src/pages/school/OnlineClasses.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button, ButtonGroup,
  Form, InputGroup, Modal, FloatingLabel, Badge, Spinner
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy
} from "firebase/firestore";
import { db } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";

/* ---------------- helpers ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY, hh:mm A") =>
  d && dayjs(d).isValid() ? dayjs(d).format(f) : "—";

/* ---------------- form defaults ---------------- */
const DEFAULT = {
  title: "",
  subject: "",
  className: "",
  section: "",
  startAt: "",
  endAt: "",
  durationMin: "",
  platform: "meet", // meet | zoom | teams | other
  meetingURL: "",
  hostId: "",       // teacher document id
  host: "",         // display name (fallback/custom)
  notes: "",
  status: "active", // active | inactive
};

/* ---------------- form modal ---------------- */
function OnlineClassFormModal({
  show,
  onHide,
  initial,
  onSave,
  classes = [],
  teachers = [],
  subjects = [],
}) {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);

  // ---- Fallbacks when Firestore classes are empty ----
  const CLASS_FALLBACK = useMemo(() => ([
    // School classes
    ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`),

    // Foundation 6–10
    "Foundation 6", "Foundation 7", "Foundation 8", "Foundation 9", "Foundation 10",

    // Competitive (general)
    "NEET", "JEE",

    // Competitive (class-specific)
    "Class 11 NEET", "Class 11 JEE", "Class 12 NEET", "Class 12 JEE",

    // Other tracks
    "Integrated", "LT",
  ]), []);

  const SECTION_FALLBACK = useMemo(() => (["A", "B", "C", "D", "E","F","G","H","I","J"]), []);

  useEffect(() => {
    if (initial) {
      setForm({
        title: initial.title || "",
        subject: initial.subject || "",
        className: initial.className || "",
        section: initial.section || "",
        startAt: initial.startAt ? dayjs(toDate(initial.startAt)).format("YYYY-MM-DDTHH:mm") : "",
        endAt: initial.endAt ? dayjs(toDate(initial.endAt)).format("YYYY-MM-DDTHH:mm") : "",
        durationMin: initial.durationMin || "",
        platform: initial.platform || "meet",
        meetingURL: initial.meetingURL || "",
        hostId: initial.hostId || "",
        host: initial.host || "",
        notes: initial.notes || "",
        status: initial.status || "active",
      });
    } else {
      setForm(DEFAULT);
    }
  }, [initial, show]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ---------- Options ----------
  // Prefer Firestore classes; otherwise fallback
  const { classNames, usingFallback } = useMemo(() => {
    const fromFs = (classes || [])
      .map(c => (c.className || c.name || "").trim())
      .filter(Boolean);
    const uniqFs = Array.from(new Set(fromFs)).sort();
    if (uniqFs.length) {
      return { classNames: uniqFs, usingFallback: false };
    }
    return { classNames: CLASS_FALLBACK, usingFallback: true };
  }, [classes, CLASS_FALLBACK]);

  const sectionOptions = useMemo(() => {
    if (!form.className) return [];
    if (!usingFallback) {
      const secs = (classes || [])
        .filter(c => (c.className || c.name) === form.className)
        .map(c => (c.section || "").trim())
        .filter(Boolean);
      return Array.from(new Set(secs)).sort();
    }
    // When using fallback classes, provide a sensible default section list
    return SECTION_FALLBACK;
  }, [classes, form.className, usingFallback, SECTION_FALLBACK]);

  const teacherOptions = useMemo(() => {
    return (teachers || [])
      .map(t => ({
        id: t.id,
        name: t.name || "",
        email: t.email || "",
        primarySubject: t.primarySubject || ""
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [teachers]);

  // Subjects (datalist), filtered by class if subject docs store className
  const subjectOptions = useMemo(() => {
    const list = (subjects || [])
      .filter(s => {
        if (!s?.name) return false;
        // If class-specific in your schema, filter by selected class
        if (s.className && form.className) return s.className === form.className;
        return true;
      })
      .map(s => (s.name || "").trim())
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [subjects, form.className]);

  // ---------- Handlers ----------
  const onTeacherChange = (teacherId) => {
    const t = teacherOptions.find(x => x.id === teacherId);
    change("hostId", teacherId);
    change("host", t ? t.name : "");
    if (!form.subject && t?.primarySubject) change("subject", t.primarySubject);
  };

  const onClassChange = (v) => {
    // Reset section when class changes
    setForm(f => ({ ...f, className: v, section: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return Swal.fire({ icon: "warning", title: "Title is required" });
    if (!form.className.trim()) return Swal.fire({ icon: "warning", title: "Class is required" });
    if (!form.startAt) return Swal.fire({ icon: "warning", title: "Start time is required" });

    if (form.meetingURL && !/^https?:\/\//i.test(form.meetingURL.trim())) {
      return Swal.fire({ icon: "info", title: "Add http:// or https:// to meeting link" });
    }

    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        subject: form.subject.trim(),
        className: form.className.trim(),
        section: form.section.trim(),
        startAt: form.startAt ? new Date(form.startAt) : null,
        endAt: form.endAt ? new Date(form.endAt) : null,
        durationMin: form.durationMin ? Number(form.durationMin) : null,
        platform: form.platform,
        meetingURL: form.meetingURL.trim(),
        hostId: form.hostId || "",
        host: form.host.trim(),
        notes: form.notes.trim(),
        status: form.status || "active",
      });
      onHide?.();
    } finally {
      setSaving(false);
    }
  };

  // ---------- UI ----------
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{initial ? "Edit Online Class" : "Schedule Online Class"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={8}>
              <FloatingLabel label="Title *">
                <Form.Control
                  value={form.title}
                  onChange={(e) => change("title", e.target.value)}
                  placeholder="Algebra – Quadratic Equations"
                  required
                />
              </FloatingLabel>
            </Col>

            <Col md={4}>
              <FloatingLabel label="Subject">
                <Form.Control
                  list="subjects-list"
                  value={form.subject}
                  onChange={(e) => change("subject", e.target.value)}
                  placeholder="Mathematics"
                />
                <datalist id="subjects-list">
                  {subjectOptions.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </FloatingLabel>
              <div className="form-text">Pick from list or type a custom subject.</div>
            </Col>

            <Col md={3}>
              <FloatingLabel label="Class *">
                <Form.Select
                  value={form.className}
                  onChange={(e) => onClassChange(e.target.value)}
                  required
                >
                  <option value="">— Select class —</option>
                  {classNames.map(c => <option key={c} value={c}>{c}</option>)}

                  {/* Preserve an existing custom value when editing */}
                  {form.className &&
                    !classNames.includes(form.className) && (
                      <option value={form.className}>{form.className} (custom)</option>
                  )}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={3}>
              <FloatingLabel label="Section">
                <Form.Select
                  value={form.section}
                  onChange={(e) => change("section", e.target.value)}
                  disabled={!sectionOptions.length}
                >
                  <option value="">{sectionOptions.length ? "— Select section —" : "— none —"}</option>
                  {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={3}>
              <FloatingLabel label="Status">
                <Form.Select
                  value={form.status}
                  onChange={(e) => change("status", e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={3}>
              <FloatingLabel label="Platform">
                <Form.Select
                  value={form.platform}
                  onChange={(e) => change("platform", e.target.value)}
                >
                  <option value="meet">Google Meet</option>
                  <option value="zoom">Zoom</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="other">Other</option>
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={6}>
              <FloatingLabel label="Start at *">
                <Form.Control
                  type="datetime-local"
                  value={form.startAt}
                  onChange={(e) => change("startAt", e.target.value)}
                  required
                />
              </FloatingLabel>
            </Col>

            <Col md={6}>
              <FloatingLabel label="End at">
                <Form.Control
                  type="datetime-local"
                  value={form.endAt}
                  onChange={(e) => change("endAt", e.target.value)}
                />
              </FloatingLabel>
            </Col>

            <Col md={8}>
              <FloatingLabel label="Meeting link / URL">
                <Form.Control
                  type="url"
                  value={form.meetingURL}
                  onChange={(e) => change("meetingURL", e.target.value)}
                  placeholder="https://meet.google.com/..."
                />
              </FloatingLabel>
            </Col>

            <Col md={4}>
              <FloatingLabel label="Duration (minutes)">
                <Form.Control
                  type="number"
                  min="0"
                  value={form.durationMin}
                  onChange={(e) => change("durationMin", e.target.value)}
                  placeholder="45"
                />
              </FloatingLabel>
            </Col>

            <Col md={6}>
              <FloatingLabel label="Host (teacher)">
                <Form.Select
                  value={form.hostId}
                  onChange={(e) => onTeacherChange(e.target.value)}
                >
                  <option value="">— Select teacher —</option>
                  {teacherOptions.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.email ? ` (${t.email})` : ""}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
              <div className="form-text">
                Or leave blank and type a custom host name below (optional)
              </div>
            </Col>

            <Col md={6}>
              <FloatingLabel label="Custom host name (optional)">
                <Form.Control
                  value={form.host}
                  onChange={(e) => change("host", e.target.value)}
                  placeholder="Ms. Priya"
                />
              </FloatingLabel>
            </Col>

            <Col md={12}>
              <FloatingLabel label="Notes / Description">
                <Form.Control
                  as="textarea"
                  style={{ height: 100 }}
                  value={form.notes}
                  onChange={(e) => change("notes", e.target.value)}
                  placeholder="Short description, prerequisites, attachments link, etc."
                />
              </FloatingLabel>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}



/* ---------------- main page ---------------- */
export default function OnlineClasses() {
  const { school, loading: loadingSchool } = useSchool();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // referenced data
  const [classesData, setClassesData] = useState([]);
  const [teachersData, setTeachersData] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]); // NEW

  // filters
  const [qText, setQText] = useState("");
 const [cls, setCls] = useState("all");
  const [sec, setSec] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const searchRef = useRef(null);

  // live online classes
  useEffect(() => {
    if (!school?.id) return;
    setLoading(true);
    const col = collection(db, "schools", school.id, "onlineClasses");
    const unsub = onSnapshot(
      query(col, orderBy("startAt", "desc")),
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            title: data.title || "—",
            subject: data.subject || "",
            className: data.className || "",
            section: data.section || "",
            startAt: toDate(data.startAt),
            endAt: toDate(data.endAt),
            durationMin: data.durationMin || null,
            platform: data.platform || "meet",
            meetingURL: data.meetingURL || "",
            hostId: data.hostId || "",
            host: data.host || "",
            notes: data.notes || "",
            status: data.status || "active",
            createdAt: toDate(data.createdAt),
          };
        });
        setRows(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [school?.id]);

  // subscribe classes, teachers, subjects
  useEffect(() => {
    if (!school?.id) return;

    // classes
    const colClasses = collection(db, "schools", school.id, "classes");
    const unsubClasses = onSnapshot(query(colClasses, orderBy("className", "asc")), (snap) => {
      const list = snap.docs.map(d => {
        const data = d.data() || {};
        return {
          id: d.id,
          className: data.className || data.name || "",
          section: data.section || "",
        };
      });
      setClassesData(list);
    });

    // teachers
    const colTeachers = collection(db, "schools", school.id, "teachers");
    const unsubTeachers = onSnapshot(query(colTeachers, orderBy("name", "asc")), (snap) => {
      const list = snap.docs.map(d => {
        const data = d.data() || {};
        return {
          id: d.id,
          name: data.name || "",
          email: data.email || "",
          primarySubject: data.primarySubject || "",
        };
      });
      setTeachersData(list);
    });

    // NEW: subjects
    const colSubjects = collection(db, "schools", school.id, "subjects");
    const unsubSubjects = onSnapshot(query(colSubjects, orderBy("name", "asc")), (snap) => {
      const list = snap.docs.map(d => {
        const data = d.data() || {};
        return {
          id: d.id,
          name: data.name || "",          // required
          code: data.code || "",          // optional
          className: data.className || "",// optional (for class-specific subjects)
        };
      });
      setSubjectsData(list);
    });

    return () => {
      unsubClasses();
      unsubTeachers();
      unsubSubjects();
    };
  }, [school?.id]);

  // filter options from rows
  const classesList = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.className && s.add(r.className));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const sectionsList = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.section && s.add(r.section));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = qText.trim().toLowerCase();
    return rows.filter((r) => {
      if (cls !== "all" && r.className !== cls) return false;
      if (sec !== "all" && r.section !== sec) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (q) {
        const hay = [
          r.title, r.subject, r.className, r.section, r.host, r.meetingURL, r.notes
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, qText, cls, sec, statusFilter]);

  const openCreate = () => { setEditing(null); setShowModal(true); };
  const openEdit = (row) => { setEditing(row); setShowModal(true); };

  // Create or update Firestore (single)
  const save = async (payload) => {
    const base = {
      title: payload.title,
      subject: payload.subject,
      className: payload.className,
      section: payload.section,
      startAt: payload.startAt || null,
      endAt: payload.endAt || null,
      durationMin: payload.durationMin || null,
      platform: payload.platform || "meet",
      meetingURL: payload.meetingURL || "",
      hostId: payload.hostId || "",
      host: payload.host || "",
      notes: payload.notes || "",
      status: payload.status || "active",
      updatedAt: serverTimestamp(),
    };

    if (editing) {
      await updateDoc(doc(db, "schools", school.id, "onlineClasses", editing.id), base);
      Swal.fire({ icon: "success", title: "Updated", timer: 900, showConfirmButton: false });
    } else {
      await addDoc(collection(db, "schools", school.id, "onlineClasses"), {
        ...base,
        createdAt: serverTimestamp(),
      });
      Swal.fire({ icon: "success", title: "Scheduled", timer: 900, showConfirmButton: false });
    }
  };

  const removeRow = async (row) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete online class?",
      text: `Remove "${row.title}" (${row.className}${row.section ? "-" + row.section : ""}).`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;
    await deleteDoc(doc(db, "schools", school.id, "onlineClasses", row.id));
    Swal.fire({ icon: "success", title: "Deleted", timer: 900, showConfirmButton: false });
  };

  const exportCSV = () => {
    const data = filtered.map((r) => {
      const teacher = teachersData.find(t => t.id === r.hostId);
      const hostDisplay = teacher ? `${teacher.name}${teacher.email ? ` (${teacher.email})` : ""}` : (r.host || "");
      return {
        id: r.id,
        title: r.title,
        subject: r.subject,
        class: r.className,
        section: r.section,
        startAt: fmt(r.startAt, "YYYY-MM-DD HH:mm"),
        endAt: fmt(r.endAt, "YYYY-MM-DD HH:mm"),
        durationMin: r.durationMin || "",
        platform: r.platform,
        meetingURL: r.meetingURL,
        host: hostDisplay,
        status: r.status,
        createdAt: fmt(r.createdAt, "YYYY-MM-DD HH:mm"),
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "online_classes");
    XLSX.writeFile(wb, "online_classes_export.csv");
  };

  if (loadingSchool) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading…
        </Card.Body>
      </Card>
    );
  }
  if (!school?.id) return <div className="text-muted">No school selected.</div>;

  return (
    <Container fluid className="p-0">
      {/* Header */}
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <h5 className="mb-0">Online Classes</h5>
          <small className="text-muted">Schedule & manage online classes for {school.name}</small>
        </Col>
        <Col className="text-end">
          <ButtonGroup>
            <Button variant="outline-secondary" onClick={exportCSV}>
              <i className="bi bi-download me-1" /> Export CSV
            </Button>
            <Button onClick={openCreate}>
              <i className="bi bi-plus-lg me-1" /> Schedule Class
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      <Card className="p-3">
        <Row className="g-2 align-items-end mb-2">
          <Col md={5}>
            <Form.Label className="mb-1">Search</Form.Label>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                ref={searchRef}
                placeholder="Title, subject, host, notes…"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
              {qText && (
                <Button variant="outline-secondary" onClick={() => setQText("")}>Clear</Button>
              )}
            </InputGroup>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Class</Form.Label>
            <Form.Select value={cls} onChange={(e) => setCls(e.target.value)}>
              {classesList.map((c) => <option key={c} value={c}>{c === "all" ? "All" : c}</option>)}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Section</Form.Label>
            <Form.Select value={sec} onChange={(e) => setSec(e.target.value)}>
              {sectionsList.map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Status</Form.Label>
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Col>
          <Col md={1} className="text-end">
            <Badge bg="light" text="dark" className="border">
              {filtered.length} / {rows.length}
            </Badge>
          </Col>
        </Row>

        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead>
              <tr>
                <th>Title</th>
                <th className="d-none d-lg-table-cell">Subject</th>
                <th>Class</th>
                <th className="d-none d-md-table-cell">Start</th>
                <th className="d-none d-xl-table-cell">End</th>
                <th className="d-none d-xl-table-cell">Host</th>
                <th className="d-none d-xl-table-cell">Platform</th>
                <th className="d-none d-xl-table-cell">Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    <Spinner animation="border" size="sm" /> Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">No online classes found</td>
                </tr>
              ) : (
                filtered.map((c) => {
                  const isPast = c.endAt
                    ? dayjs(c.endAt).isBefore(dayjs())
                    : dayjs(c.startAt).isBefore(dayjs());
                  const teacher = teachersData.find(t => t.id === c.hostId);
                  const hostDisplay = teacher
                    ? `${teacher.name}${teacher.email ? ` (${teacher.email})` : ""}`
                    : (c.host || "—");

                  let hostDomain = "";
                  try { hostDomain = c.meetingURL ? new URL(c.meetingURL).hostname : ""; } catch {}

                  return (
                    <tr key={c.id}>
                      <td>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: 280 }}>{c.title}</div>
                        <div className="text-muted small">{hostDomain}</div>
                      </td>
                      <td className="d-none d-lg-table-cell">{c.subject || "—"}</td>
                      <td>{[c.className, c.section].filter(Boolean).join("-") || "—"}</td>
                      <td className="d-none d-md-table-cell">{fmt(c.startAt)}</td>
                      <td className="d-none d-xl-table-cell">{fmt(c.endAt)}</td>
                      <td className="d-none d-xl-table-cell">{hostDisplay}</td>
                      <td className="d-none d-xl-table-cell text-capitalize">{c.platform}</td>
                      <td className="d-none d-xl-table-cell">
                        {c.status === "inactive" ? (
                          <Badge bg="secondary">Inactive</Badge>
                        ) : isPast ? (
                          <Badge bg="dark">Ended</Badge>
                        ) : (
                          <Badge bg="success">Active</Badge>
                        )}
                      </td>
                      <td className="text-end">
                        {c.meetingURL && (
                          <>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-2"
                              title="Open link"
                              onClick={() => window.open(c.meetingURL, "_blank")}
                            >
                              <i className="bi bi-box-arrow-up-right" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-2"
                              title="Copy link"
                              onClick={() => navigator.clipboard.writeText(c.meetingURL).catch(() => {})}
                            >
                              <i className="bi bi-clipboard" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-2"
                          title="Edit"
                          onClick={() => { setEditing(c); setShowModal(true); }}
                        >
                          <i className="bi bi-pencil" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          title="Delete"
                          onClick={() => removeRow(c)}
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit modal */}
      <OnlineClassFormModal
        show={showModal}
        onHide={() => { setShowModal(false); setEditing(null); }}
        initial={editing}
        onSave={async (payload) => {
          await save(payload);
          setShowModal(false);
          setEditing(null);
        }}
        classes={classesData}
        teachers={teachersData}
        subjects={subjectsData}   // NEW
      />

      <style>{`
        .card { border-radius: 14px; }
        .form-control, .form-select { border-radius: 10px; }
      `}</style>
    </Container>
  );
}
